import { all, takeEvery, put, select } from 'redux-saga/effects';
import { userService, iubendaService } from 'services/api';
import {
  Subject,
  LegalNotice,
  User,
  HTTPError,
  PrivateUser,
} from 'services/api/entities';
import songsApi from 'services/api/songs/songsApi';
import { startAction, stopAction, errorAction } from '../base/actions';
import { userReset, userSuccess } from '../user/actions';
import { SongsStore } from '../songs';
import { Action } from '../types';
import type { LoginData } from './types';
import { loginSuccess, logoutSuccess } from './actions';
import authActionNames from './actions/constants';

const getAccessTokenFor = (
  actionType: string,
  data: LoginData
): Promise<User> => {
  if (actionType === authActionNames.LOGIN_WITH_CREDENTIALS) {
    const { email, password, cloudflareToken } = data as {
      email: string;
      password: string;
      cloudflareToken: string;
    };
    return userService.loginWithCredentials(email, password, cloudflareToken);
  }
  if (actionType === authActionNames.LOGIN_WITH_GOOGLE) {
    const { token } = data as { token: string };
    return userService.loginWithGoogle(token);
  }
  if (actionType === authActionNames.LOGIN_WITH_FACEBOOK) {
    const { token } = data as { token: string };
    return userService.loginWithFacebook(token);
  }
  throw HTTPError.invalidData();
};

function* addPendingSongToLibraryIfNeeded() {
  const songsStore: SongsStore = yield select((store) => store.songs);

  if (songsStore.pendingForLibrary) {
    const { songId } = songsStore.pendingForLibrary;

    // @ts-ignore
    yield put(songsApi.endpoints.addToLibrary.initiate({ songId }));
  }
}

/**
 * Request to get access token
 * @param action
 */
export function* asyncGetAccessToken(action: {
  type: string;
  payload: { data: LoginData };
}) {
  const { data } = action.payload;
  yield put(startAction(action));
  try {
    const user: PrivateUser = yield getAccessTokenFor(action.type, data);
    yield put(userSuccess(user));
    yield put(loginSuccess());
    yield addPendingSongToLibraryIfNeeded();
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/**
 * Logs user's consent in Iubenda database
 * @param user
 * @param consentPreference
 */
export function* createConsent(
  user: User,
  consentPreference: { general: boolean }
) {
  const subject = Subject.createUnverified(user);
  const legalNotices = [LegalNotice.privacyPolicy()];

  yield iubendaService
    .createConsent(subject, consentPreference, legalNotices)

    .then(() => {});
}

/**
 * Request to get access token with credentials
 * @param action
 */
export function* asyncRegisterAndLogin(action: {
  type: string;
  payload: {
    userData: { email: string; password: string; cloudflareToken: string };
    consentPreference: { general: boolean };
  };
}) {
  const { userData, consentPreference } = action.payload;

  yield put(startAction(action));
  try {
    const { email, password, cloudflareToken } = userData;
    const user: PrivateUser = yield userService.registerAndLogin(
      email,
      password,
      cloudflareToken
    );
    yield put(userSuccess(user));
    yield put(loginSuccess());
    yield addPendingSongToLibraryIfNeeded();

    yield createConsent(user, consentPreference);
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/**
 * Request to get access token with refresh token
 */
export function* asyncLoginToApp(action: Action<void>) {
  yield put(startAction(action));
  try {
    const user: PrivateUser | null = yield userService.login();
    if (!user) {
      return;
    }
    yield put(userSuccess(user));
    yield put(loginSuccess());
  } catch (error) {
    yield put(errorAction({ type: action.type, payload: error as HTTPError }));
  } finally {
    yield put(stopAction(action));
  }
}

/**
 * Logout from network layer
 */
export function* asyncLogout(action: Action<void>) {
  yield put(startAction(action));
  userService.logout();
  yield put(logoutSuccess());
  yield put(userReset());
  yield put(stopAction(action));
}

/* ----------------------------------------------------------------------------------------
 * Watch sagas
 * ----------------------------------------------------------------------------------------*/

export function* watchGetAccessToken() {
  yield takeEvery(
    [
      authActionNames.LOGIN_WITH_CREDENTIALS,
      authActionNames.LOGIN_WITH_GOOGLE,
      authActionNames.LOGIN_WITH_FACEBOOK,
    ],
    asyncGetAccessToken
  );
}

export function* watchRegisterAndLoginToApp() {
  yield takeEvery(authActionNames.REGISTER_AND_LOGIN, asyncRegisterAndLogin);
}

export function* watchLoginToApp() {
  yield takeEvery(authActionNames.LOGIN_TO_APP, asyncLoginToApp);
}

export function* watchLogout() {
  yield takeEvery(authActionNames.LOGOUT, asyncLogout);
}

export default function* rootSaga() {
  yield all([
    watchGetAccessToken(),
    watchLoginToApp(),
    watchRegisterAndLoginToApp(),
    watchLogout(),
  ]);
}
